import { createModule } from 'terrific-singleton';
import { TerrificSpec } from 'terrific-singleton/terrific-module';
import * as ProductCompareHelper from '@project/shared/utils/product-compare/js/product-compare';
import { IProductCompareItem } from '@covestro/models';

const classes = {
	heading: 'm-product-compare-layer__heading',
	quantity: 'm-product-compare-layer__number',
	switch: 'm-product-compare-layer__switch',
	product: 'm-product-compare-layer__product',
	closeButton: 'js-m-product-compare-layer__close',
	deleteButton: 'm-product-compare-layer__delete',
	activeClass: 'state-m-product-compare-layer--active',
	collapseClass: 'state-m-product-compare-layer--open',
	compareList: 'm-product-compare-layer__list',
	preloader: '.m-product-compare-layer__product .a-loading-indicator',
};

@createModule('ProductCompareLayer')
/**
 * product-compare-layer module implementation.
 *
 * @author Lars-Olof Kreim <lars-olof.kreim@namics.com>
 */
export class ProductCompareLayer extends TerrificSpec {
	oneItemText: string;
	multiItemText: string;
	compareProductList: IProductCompareItem[]; // define the varaiable.
	$ctx: JQuery;
	/**
	 * start method, called on terrific bootstap
	 *
	 * @param {Object} resolve  function to call at the end of component start
	 * @returns {void}
	 */
	start(resolve: Function) {
		this.$ctx = $(this._ctx);
		this.oneItemText = this.$ctx.data('one-item-text');
		this.multiItemText = this.$ctx.data('multi-item-text');
		this.$ctx.on('hideLayer', this.hideLayer.bind(this));
		this.$ctx.on('showLayer', (e: any, count: number) => {
			this.showLayer(count);
		});

		this.compareProductList = []; // the data for pop list.

		// to monitor the add action from product finder list, to receive the name id market from this event.
		this.$ctx.on('addNewProductLayer', (e: any, product: IProductCompareItem) => {
			this.addNewCompareProductItem(product);
		});

		// to monitor the remove action from product finder list, to receive the name id market from this event.
		this.$ctx.on('removeNewProductLayer', (e: any, id: string) => {
			this.removeNewCompareProductItem(id);
		});

		this.$ctx.find(`.${classes.closeButton}`).on('click', this.onCloseClick.bind(this));
		this.$ctx.find(`.${classes.switch}`).on('click', this.onSwitchClick.bind(this));
		// this.$ctx.find(`.${classes.deleteButton}`).on('click', this.onRemoveClick.bind(this)); // empty action, so comment it.
		this.$ctx.find(classes.preloader).hide(); // to hide the loading when initial
		ProductCompareHelper.updateLayer();
		this.fetchCompareProductNameFromService(); // to fetch the name data from service when initial.
		resolve();
	}

	/**
	 * to get the the product name data from server when the page initial
	 *
	 * @returns {void}
	 */
	fetchCompareProductNameFromService() {
		const compareData = ProductCompareHelper.getCookieData();
		// if the cookie data exist, will get the name from service.
		if (compareData.length > 0) {
			this.fetchProductCompareData();
		}
	}

	/**
	 * method is triggered on close click
	 *
	 * @returns {void}
	 */
	onCloseClick() {
		this.hideLayer();
	}

	/**
	 * method hides compare layer
	 *
	 * @returns {void}
	 */
	hideLayer() {
		this.$ctx.removeClass(classes.activeClass);
		ProductCompareHelper.setLayerHiddenCookie();
		this.$ctx.removeClass(classes.collapseClass);
	}

	/**
	 * method shows compare layer
	 *
	 * @param {number} count  count of products in compare
	 * @returns {void}
	 */
	showLayer(count: number) {
		let text = this.oneItemText;
		if (count > 1) {
			text = this.multiItemText.replace('{0}', `${count}`);
		}
		const $statusText = this.$ctx.find(`.${classes.quantity}`);
		$statusText.text(text);
		this.$ctx.addClass(classes.activeClass);
	}

	/**
	 * to execute the request action by ajax.
	 *
	 * @returns {void}
	 */
	fetchProductCompareData() {
		const serviceUrl = this.$ctx.data('layer-compare-service');
		if (serviceUrl) {
			this.$ctx.find(classes.preloader).show();
			$.ajax({
				method: 'get',
				url: serviceUrl,
				success: this.onProductCompareDataSuccess.bind(this),
				error: () => {
					this.$ctx.find(classes.preloader).hide();
				},
			});
		}
	}

	/**
	 * handle response data from service.
	 *
	 * @returns {void}
	 */
	onProductCompareDataSuccess(data: any) {
		this.$ctx.find(classes.preloader).hide();
		if (data && data.length > 0) {
			this.compareProductList = [];
			const compareData = ProductCompareHelper.getCookieData();
			compareData &&
				compareData.forEach((item: any) => {
					const responseItem: IProductCompareItem = data.find(
						(dataItem: any) => dataItem.number === item.number
					);
					if (responseItem) {
						this.compareProductList.push({
							number: responseItem.number,
							market: '',
							name: responseItem.name,
						});
					}
				});
			this.handleCompareResult(this.compareProductList);
		}
	}

	/**
	 * render the compare item from list.
	 *
	 * @returns {void}
	 */
	handleCompareResult(compareProductList: IProductCompareItem[]) {
		const $selectElement = this.$ctx.find(`.${classes.compareList}`);
		$selectElement.empty();
		for (let compareItem of compareProductList) {
			$selectElement.append(`<li class="m-product-compare-layer__item">
										<span class="m-product-compare-layer__name">${compareItem.name}</span>
										<a id="${compareItem.number}" class="m-product-compare-layer__delete"></a>
									</li>`);
			$selectElement.on('click', 'li a', (event: any) => {
				const productId = $(event.target).attr('id');
				this.removeNewCompareProductItem(productId);
				ProductCompareHelper.removeProduct(productId); // to remove local data from cookies and update the layer count, remove action will check if the item exists.
				ProductCompareHelper.removeCompareProductLayerFromFinderList(productId); // to notify the change if it is in product list page now.
				ProductCompareHelper.updateLayer(); // update layer
			});
		}
	}

	/**
	 * remove new compare product item in list
	 *
	 * @returns {void}
	 */
	removeNewCompareProductItem(id: string) {
		const index = this.compareProductList.findIndex((item) => item.number === id);
		if (index > -1) {
			this.compareProductList.splice(index, 1);
			this.handleCompareResult(this.compareProductList);
		}
	}

	/**
	 * add new compare product item in list
	 *
	 * @returns {void}
	 */
	addNewCompareProductItem(product: IProductCompareItem) {
		this.compareProductList.push(product);
		this.handleCompareResult(this.compareProductList);
	}

	/**
	 * stop method, called on terrific stop
	 *
	 * @returns {void}
	 */
	stop() {}

	/**
	 * method hides compare layer
	 *
	 * @returns {void}
	 */
	onSwitchClick() {
		this.$ctx.toggleClass(classes.collapseClass);
	}

	/**
	 * method remove compare item
	 *
	 * @returns {void}
	 */
	// onRemoveClick() {
	//
	// }
}
